import { HourglassOutlined, LockOutlined, StopOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Button, Typography } from "antd";
import { useExportOffersMutation, useGetOffersQuery } from "api/TenantSlug/Offer/hooks";
import { useStoreRequestMutation } from "api/TenantSlug/Request/hooks.ts";
import { justRequestedOfferAccessAtom } from "atoms/justRequestedOfferAccessAtom.ts";
import { AddOfferDrawer } from "components/Drawers/AddOfferDrawer/AddOfferDrawer";
import { DetailCommissionDrawer } from "components/Drawers/DetailCommissionDrawer/DetailCommissionDrawer";
import { OfferCommissionsOverviewDrawer } from "components/Drawers/OfferCommissionsOverviewDrawer/OfferCommissionsOverviewDrawer";
import { DrawerLink } from "components/UI/DrawerLink";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { CountryCell } from "components/UI/Table/UI/CountryCell/CountryCell.tsx";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { LabelsCell } from "components/UI/Table/UI/LabelsCell/LabelsCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useUser } from "hooks/useUser.ts";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { CommissionType, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models.ts";
import { getOfferAccessGateState } from "utils/getOfferAccessGateState.ts";

import OfferData = App.Data.OfferData;
import GetOffersResponse = App.Http.Responses.Offer.GetOffersResponse;
import CommissionData = App.Data.CommissionData;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import CountryData = App.Data.CountryData;

interface SearchParams {
    [`detailCommission-id`]: string;
    [`offerCommissionsOverview-id`]: string;
}

export const OffersTable = () => {
    const { t } = usePaldockTranslation("tables.offers");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const userUtils = useUser();
    const permissionUtils = usePermission();
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const tableId = TableId.OfferTable;
    const tableAtomInstance = tableAtom<OfferData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const setJustRequestedOfferAccess = useSetRecoilState(justRequestedOfferAccessAtom);
    const storeRequestMutation = useStoreRequestMutation({
        onSuccess: () => {
            tableFetchDataUtils.refetchData();
        },
    });
    const activeUser = userUtils.getAuthenticatedUser();
    const requestInclude = "userAffiliates,userRequests,commissions,currency,advertiser,country,category,labels";

    const request = {
        filter: {
            filter: {
                ...(tableState.filter?.filter || {}),
            },
            include: requestInclude,
            sort: tableState?.filter?.sort ?? "title",
        },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
    };

    const query = useGetOffersQuery(request, {
        enabled: false,
    });

    const exportOffersMutation = useExportOffersMutation();
    const tableFetchDataUtils = useTableDataApiOperations<
        GetOffersResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, query, undefined, undefined, exportOffersMutation);

    const columnConfig: TableColumnType<OfferData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.logo")} />,
            dataIndex: "logo.url",
            key: "logo.url",
            render: (_, record) =>
                record.logo?.url ? (
                    <img src={record.logo.url} alt="Logo" style={{ height: "32px" }} className={"object-contain"} />
                ) : null,
            ellipsis: true,
            width: 80,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("actions")} />,
            dataIndex: "access",
            key: "access",
            render: (_, record) => {
                const gateState = getOfferAccessGateState({
                    access: record.access,
                    affiliates: record.affiliates,
                    userRequests: record.user_requests,
                    currentUserId: userUtils.getAuthenticatedUser()?.id,
                    hasUnrestrictedPermission: permissionUtils.hasPermission(
                        PermissionEnum.OfferViewAccessUnrestricted
                    ),
                });

                switch (gateState) {
                    case "granted":
                        return <></>;
                    case "restricted-pending":
                        return <HourglassOutlined />;
                    case "restricted-locked":
                        return (
                            <Button
                                type="text"
                                icon={<LockOutlined />}
                                aria-label={t("request_access")}
                                disabled={!activeUser}
                                onClick={() => {
                                    setJustRequestedOfferAccess((current) =>
                                        current.includes(record.id) ? current : [...current, record.id]
                                    );
                                    storeRequestMutation.mutate({
                                        user_id: activeUser?.id ?? "",
                                        type: "offer",
                                        offer_id: record.id,
                                    });
                                }}
                            />
                        );
                    case "private-blocked":
                        return <StopOutlined />;
                }
            },
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "title",
            key: "title",
            sorter: true,
            defaultSorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            showSorterTooltip: {
                target: "sorter-icon",
            },
            inputType: "text",
            ellipsis: true,
            width: 150,
        },
        TableColumn.Entity.Advertiser<OfferData>({
            title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
            ellipsis: true,
            width: 150,
            getEntity: (data) => data.advertiser,
        }),
        {
            title: <ColumnHeaderTitle columnTitle={tG("country")} />,
            dataIndex: "country",
            key: "country",
            render: (country?: CountryData) => (country ? <CountryCell country={country} /> : tC("all")),
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("category")} />,
            dataIndex: "category.name",
            key: "category.name",
            render: (_, data: OfferData) => data.category?.name ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.categorySelect,
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("tags")} />,
            dataIndex: "labels.name",
            key: "labels.name",
            render: (_, data) => <LabelsCell labels={data.labels} />,
            filterable: true,
            filterType: FILTER_TYPES.labelMultiSelect,
            ellipsis: false,
            width: 150,
        },
        /*
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.locality")} />,
            dataIndex: "countries",
            key: "countries",
            render: (country: CountryData) => <CountryCell country={country} />,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.epc")} />,
            dataIndex: "generaldata",
            key: "generaldata",
        },*/

        {
            title: <ColumnHeaderTitle columnTitle={t("columns.payout")} />,
            dataIndex: "commissions",
            key: "commissions",
            render: (commissions: CommissionData[] = [], record: OfferData) => {
                if (commissions.length === 0) {
                    return <Typography.Text type="secondary">–</Typography.Text>;
                }
                const userCommissions = commissions.filter(
                    (c) => c.commission_type === CommissionType.User && c.is_enabled === true
                );

                if (!userCommissions.length) {
                    return <>-</>;
                }

                userCommissions
                    .sort((a, b) => (a.weight ?? 0) - (b.weight ?? 0))
                    .sort((a, b) => {
                        const aVal = a.commission_group_id ?? "";
                        const bVal = b.commission_group_id ?? "";
                        return bVal.localeCompare(aVal);
                    })
                    .sort((a, b) => {
                        const aVal = a.conversion_source ?? "";
                        const bVal = b.conversion_source ?? "";
                        return bVal.localeCompare(aVal);
                    })
                    .sort((a, b) => {
                        const aVal = a.conversion_status ?? "";
                        const bVal = b.conversion_status ?? "";
                        return bVal.localeCompare(aVal);
                    })
                    .sort((a, b) => {
                        const aVal = a.conversion_type ?? "";
                        const bVal = b.conversion_type ?? "";
                        return bVal.localeCompare(aVal);
                    });

                //const first = userCommissions[0];
                /*const label = first.payout_placeholder
                    ? first.payout_placeholder
                    : `${first.payout_formula} ${record?.currency?.currency_code} `;
                console.log(label); //keep logic to use after BE fix*/

                return (
                    <DrawerLink
                        drawerId={DrawerId.OfferCommissionsOverview}
                        search={{
                            [`${DrawerId.OfferCommissionsOverview}-id`]: record.id ?? "",
                        }}
                        action="show"
                    >
                        <Button type="link" className={"!underline hover:!no-underline"} size={"small"}>
                            {tC("view")}
                        </Button>
                    </DrawerLink>
                );
            },
            ellipsis: true,
            width: 150,
        },
    ];

    const { getRowHandlers, getRowClassName } = useRowHandlers<OfferData>({
        actions: [
            {
                type: "link",
                url: `/$tenantSlug/offer/detail/:id/link`,
                permission: PermissionEnum.OfferView,
                condition: () => !permissionUtils.hasPermission(PermissionEnum.OfferUpdate),
            },
            {
                type: "link",
                url: `/$tenantSlug/offer/edit/:id/general`,
                permission: PermissionEnum.OfferUpdate,
            },
        ],
    });

    return (
        <>
            {permissionUtils.hasPermission(PermissionEnum.OfferCreate) && (
                <AddOfferDrawer onSubmit={() => tableFetchDataUtils.refetchData()} />
            )}

            {searchParams[`${DrawerId.DetailCommission}-id`] && (
                <DetailCommissionDrawer commissionId={searchParams[`${DrawerId.DetailCommission}-id`]} />
            )}

            {searchParams[`${DrawerId.OfferCommissionsOverview}-id`] && (
                <OfferCommissionsOverviewDrawer offerId={searchParams[`${DrawerId.OfferCommissionsOverview}-id`]} />
            )}

            <Table<GetOffersResponse, OfferData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetOffersResponse}
                dataSource={tableFetchDataUtils.tableData?.offers || []}
                columnConfig={columnConfig}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                rowKey={(record) => record.id}
                addEntityData={{
                    drawerId: DrawerId.AddOffer,
                    permission: PermissionEnum.OfferCreate,
                }}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                enableFilters={true}
                searchDataIndex="title"
                hideActionsColumn={true}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                autoScrollWidth={true}
            />
        </>
    );
};
